import { Box, Button, CircularProgress, Container, Grid2 as Grid, Icon, InputLabel, Typography } from "@mui/material";
import SkeletonImage from "../../../components/common/SkeletonImage";
import { toastError } from "../../../redux/guest-slices/other.slice";
import { useParams, NavLink, useNavigate } from "react-router-dom";
import AutoCompleteA from "../../../components/ui/AutoCompleteA";
import InputFieldA from "../../../components/ui/InputFieldA";
import SelectInput from "../../../components/ui/SelectInput";
import { useDispatch, useSelector } from "react-redux";
import { ckEditorConfig } from "../../../utils/config";
import { FILE_PATH_URI } from "../../../utils/helper";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { useEffect, useRef, useState } from "react";
import Icons from "../../../assets/js/Icons";
import { ClassicEditor } from "ckeditor5";

import {
  retrieveAllBlogCategories,
  retrieveBlog,
  updateBlog,
} from "../../../redux/admin-slices/blogs.slice";

function EditBlog() {
  const files = useRef(null);
  const editorRef = useRef(null);
  const categoryRef = useRef(null);
  const dispatch = useDispatch();
  const { blogId } = useParams();
  const { loadings } = useSelector((state) => state.otherSlice);
  const sourceRef = useRef(null);
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loading, setloading] = useState(null);
  const [blog, setBlog] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [tags, setTags] = useState([]);

  const retrieveData = async () => {
    const [categories, { blog }] = await Promise.all([
      dispatch(retrieveAllBlogCategories({ limit: 100 })).unwrap(),
      dispatch(retrieveBlog(blogId)).unwrap(),
    ]);
    const blogCategory = categories?.records?.find(
      ({ _id }) => _id === blog?.categoryId
    );
    blog.blogCategory = blogCategory;
    setBlog(blog);
    setImagePreview(FILE_PATH_URI + blog?.banner?.path);
    setCategories(categories?.records);
    setTags(blog?.tags || []);
    setloading(true);
  };

  // Handle image preview
  const handleImagePreview = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    files.current = Array.from(e.target.files);
    setImagePreview(URL.createObjectURL(file));
  };

  // Handle create blog
  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = Object.fromEntries(new FormData(form));
    formData.banner = files?.current || blog?.banner || null;
    formData.description =
      editorRef?.current?.data || blog?.description || null;
    formData.tags = tags;
    formData.categoryId =
      categoryRef?.current?.value?._id || blog?.categoryId || null;

    if (!formData.categoryId) {
      return dispatch(toastError("Category is required!"));
    }
    if (!formData.title || formData.title.trim() === "") {
      return dispatch(toastError("Title is required!"));
    }
    if (!formData.description || formData.description.trim() === "") {
      return dispatch(toastError("Description is required!"));
    }
    if (!formData.banner || (Array.isArray(formData.banner) && formData.banner.length === 0)) {
      return dispatch(toastError("Banner image is required!"));
    }
    if (!formData.source || formData.source.trim() === "") {
      return dispatch(toastError("Source is required!"));
    }
    if (!formData.tags || formData.tags.length === 0) {
      return dispatch(toastError("Tags are required!"));
    }

    dispatch(updateBlog({ formData, id: blogId }))
      .unwrap()
      .then(() => {
        form.reset();
        navigate("/blogs/all");
      })
      .catch(() => { });
  };

  useEffect(() => {
    retrieveData();

    // eslint-disable-next-line
  }, []);

  return (
    loading && (
      <Container maxWidth={false} fixed sx={{ py: 5 }}>
        <Box className="overview-container">
          <Typography variant="pageTitle">
            {blogId ? "Edit" : "Add new"} blog post
          </Typography>
          <Button
            variant="outlined"
            to="/blogs/all"
            LinkComponent={NavLink}
            startIcon={<Icons.ListSolid />}
          >
            View All Blogs
          </Button>
        </Box>

        <Box className="page-content">
          <Grid container spacing={3} component="form" onSubmit={handleSubmit}>
            <Grid size={{ xs: 12, xl: 6 }}>
              <SelectInput
                label="Category *"
                renderValueKey="name"
                inputRef={categoryRef}
                placeholder="Select Category"
                selectLabel="Select Category"
                loading={loadings?.retrieveAll}
                options={categories || []}
                defaultValue={blog?.blogCategory || "[object Object]"}
              />
            </Grid>
            <Grid size={{ xs: 12, xl: 6 }}>
              <InputFieldA
                name="title"
                label="Title *"
                placeholder="Enter Title"
                value={blog?.title || ""}
              />
            </Grid>
            <Grid size={12} sx={{ ".ck-content": { minHeight: "200px" } }}>
              <CKEditor
                ref={editorRef}
                editor={ClassicEditor}
                config={ckEditorConfig}
                data={blog?.description || ""}
                onChange={(_, editor) => {
                  editorRef.current.data = editor.getData();
                }}
              />
            </Grid>
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size="grow">
                  <Box
                    sx={{
                      height: "100%",
                      maxHeight: 300,
                      border: "1px dashed",
                      borderRadius: "8px",
                      placeContent: "center",
                      borderColor: "primary.main",
                      display: imagePreview ? "block" : "grid",
                    }}
                  >
                    {imagePreview ? (
                      <SkeletonImage
                        sx={{ objectFit: "contain" }}
                        src={imagePreview}
                        alt="Blog banner"
                      />
                    ) : (
                      <>
                        <Icon
                          sx={{
                            fontSize: "100px",
                            color: "other.color2",
                            m: "auto",
                            svg: { display: "block" },
                          }}
                        >
                          <Icons.ImageSolid />
                        </Icon>
                        <Typography mb={1.5} color="other.color2">
                          Upload banner image
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
                <Grid size="auto">
                  <InputLabel
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      gap: "8px",
                      color: "white",
                      display: "flex",
                      fontSize: "18px",
                      minWidth: "150px",
                      borderRadius: "4px",
                      alignItems: "center",
                      bgcolor: "primary.main",
                      justifyContent: "center",
                      input: { display: "none" },
                    }}
                  >
                    <input
                      onChange={handleImagePreview}
                      accept="image/*"
                      type="file"
                    />
                    {/*  */}
                    <Icon sx={{ fontSize: "24px" }}>
                      <Icons.UploadSolid />
                    </Icon>
                    Upload
                  </InputLabel>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, xl: 6 }}>
              <InputFieldA
                name="source"
                label="Source"
                placeholder="Enter source"
                defaultValue={blog?.source || ""}
                inputRef={sourceRef}
              />
            </Grid>
            <Grid size={{ xs: 12, xl: 6 }}>
              <AutoCompleteA
                multiple
                freeSolo
                name="tags"
                value={tags}
                label="Tags"
                placeholder="Enter tags"
                onChange={(_, value) => {
                  setTags(value);
                }}
              />
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                sx={{ minWidth: "150px" }}
                disabled={loadings?.create || loadings?.update}
                endIcon={
                  (loadings?.create || loadings?.update) && (
                    <CircularProgress size={22} color="inherit" thickness={6} />
                  )
                }
              >
                {blogId ? "Update" : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>
    )
  );
}

export default EditBlog;
